import React, { useState } from 'react';
import axios from 'axios';

/**
 * VARReviewModal
 *
 * Two-phase modal for VAR review:
 * Phase 1: Start review - select type, description, send to overlay
 * Phase 2: Decision - confirm or overturn, send result
 */
export const VARReviewModal = ({ isOpen, event, matchId, onClose }) => {
  const [phase, setPhase] = useState('start'); // 'start' | 'decision'
  const [reviewType, setReviewType] = useState('');
  const [description, setDescription] = useState('');
  const [decisionDescription, setDecisionDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`;

  // Auto-detect review type from event
  const getDefaultReviewType = () => {
    if (!event) return 'goal';
    const type = event.event_type?.toLowerCase() || '';
    if (type.includes('goal') || type.includes('penalty_goal')) return 'goal';
    if (type.includes('penalty')) return 'penalty';
    if (type.includes('red')) return 'red_card';
    return 'goal';
  };

  // Auto-generate description
  const getDefaultDescription = (type) => {
    const descriptions = {
      goal: 'CHECKING GOAL',
      penalty: 'CHECKING PENALTY',
      red_card: 'CHECKING RED CARD',
      identity: 'CHECKING IDENTITY'
    };
    return descriptions[type] || 'CHECKING';
  };

  // Initialize defaults when modal opens with new event
  React.useEffect(() => {
    if (isOpen && event) {
      const type = getDefaultReviewType();
      setReviewType(type);
      setDescription(getDefaultDescription(type));
      setDecisionDescription('');
      setPhase('start');
      setLoading(false);
    }
  }, [isOpen, event?.id]);

  if (!isOpen || !event) return null;

  const playerName = event.player
    ? `${event.player.first_name} ${event.player.last_name}`
    : 'Unknown Player';

  const handleStartReview = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/overlay/${matchId}/var-start`, {
        eventId: event.id,
        reviewType,
        description: description || getDefaultDescription(reviewType)
      });
      setPhase('decision');
      setDecisionDescription('');
    } catch (error) {
      console.error('Error starting VAR review:', error);
      alert('Failed to start VAR review');
    } finally {
      setLoading(false);
    }
  };

  const getDecisionDefault = (decision) => {
    const typeLabel = reviewType.toUpperCase().replace('_', ' ');
    return decision === 'confirmed'
      ? `${typeLabel} CONFIRMED`
      : `NO ${typeLabel} - OVERTURNED`;
  };

  const handleDecision = async (decision) => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/overlay/${matchId}/var-decision`, {
        eventId: event.id,
        decision,
        description: decisionDescription || getDecisionDefault(decision)
      });
      handleClose();
    } catch (error) {
      console.error('Error submitting VAR decision:', error);
      alert('Failed to submit VAR decision');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReview = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/overlay/${matchId}/var-decision`, {
        eventId: event.id,
        decision: 'cancelled',
        description: 'Review cancelled'
      });
    } catch (error) {
      console.error('Error cancelling VAR review:', error);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const handleClose = () => {
    setPhase('start');
    setLoading(false);
    onClose();
  };

  const reviewTypes = [
    { value: 'goal', label: 'Goal' },
    { value: 'penalty', label: 'Penalty' },
    { value: 'red_card', label: 'Red Card' },
    { value: 'identity', label: 'Identity' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
        {/* Header */}
        <div className="p-6 border-b bg-blue-900 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold flex items-center gap-2">
              VAR Review
            </h3>
            <span className="text-sm opacity-80">
              {phase === 'start' ? 'Step 1: Start Review' : 'Step 2: Decision'}
            </span>
          </div>
        </div>

        {/* Event Info */}
        <div className="p-4 bg-gray-50 border-b">
          <div className="flex items-center justify-between text-sm">
            <div>
              <span className="font-semibold">{playerName}</span>
              <span className="text-gray-500 ml-2">({event.team?.name || event.team?.display_name || 'Unknown'})</span>
            </div>
            <div className="text-gray-600">
              <span className="font-mono font-bold">{event.minute}'</span>
              <span className="ml-1">{event.half === 1 ? '1st Half' : '2nd Half'}</span>
            </div>
          </div>
          <div className="text-sm text-gray-500 mt-1">
            Event: {event.event_type?.replace(/_/g, ' ')}
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {phase === 'start' ? (
            <>
              {/* Review Type */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Review Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {reviewTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setReviewType(type.value);
                        setDescription(getDefaultDescription(type.value));
                      }}
                      className={`px-3 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${
                        reviewType === type.value
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Overlay Text
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="CHECKING GOAL"
                />
              </div>

              {/* Start Button */}
              <button
                onClick={handleStartReview}
                disabled={loading}
                className="w-full px-4 py-3 bg-blue-700 text-white rounded-lg font-bold text-lg hover:bg-blue-800 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Starting...' : 'Start VAR Review'}
              </button>
            </>
          ) : (
            <>
              {/* Review In Progress */}
              <div className="text-center mb-6">
                <div className="text-lg font-bold text-blue-800 animate-pulse">
                  VAR Review In Progress...
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  The overlay is showing the review banner
                </p>
              </div>

              {/* Decision Description */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Decision Text (shown on overlay)
                </label>
                <input
                  type="text"
                  value={decisionDescription}
                  onChange={(e) => setDecisionDescription(e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`Leave empty for auto text`}
                />
                {!decisionDescription && (
                  <div className="mt-1 text-xs text-gray-400 space-y-0.5">
                    <div>Confirm: <span className="font-semibold">{getDecisionDefault('confirmed')}</span></div>
                    <div>Overturn: <span className="font-semibold">{getDecisionDefault('overturned')}</span></div>
                  </div>
                )}
              </div>

              {/* Decision Buttons */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <button
                  onClick={() => handleDecision('confirmed')}
                  disabled={loading}
                  className="px-4 py-4 bg-green-600 text-white rounded-lg font-bold text-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? '...' : 'CONFIRM'}
                </button>
                <button
                  onClick={() => handleDecision('overturned')}
                  disabled={loading}
                  className="px-4 py-4 bg-red-600 text-white rounded-lg font-bold text-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? '...' : 'OVERTURN'}
                </button>
              </div>

              {/* Cancel Review */}
              <button
                onClick={handleCancelReview}
                disabled={loading}
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 transition-colors text-sm"
              >
                Cancel Review (hide overlay)
              </button>
            </>
          )}
        </div>

        {/* Footer - Close */}
        <div className="p-4 border-t flex justify-end">
          <button
            onClick={phase === 'decision' ? handleCancelReview : handleClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-semibold text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default VARReviewModal;
